import { useEffect, useMemo, useState } from "react";
import { FetcherPaginated, FetchParams } from "@shared/types";
import { RequestCriteriaType } from "../../Services/PostService.types";
import { useReferencedState } from "../useReferencedState";
import { CommentCursorableType, PostCursorableType } from "./cursor.types";

const DEFAULT_LIMIT = 10;

export function useCursoredInfiniteScroll<T>(
    fetcher: FetcherPaginated<T>,
    fetchParams: FetchParams,
    preparedParams: object = {},
    maxItemCount = 5000,
) {
    const [isInitDone, setIsInitDone] = useState(false);
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useReferencedState<Array<T>>([]);
    const [hasNextPage, setHasNextPage] = useState<boolean>(true);
    const [error, setError] = useState<Error>();
    const [sortCriteria, setSortCriteria] = useReferencedState(
        fetchParams.sortCriteria ?? {},
    );

    useEffect(() => {
        setLoading(false);
    }, [items.current]);

    const updatedFetchParams: FetchParams = useMemo(() => {
        const resolvedSortCriteria: RequestCriteriaType = {
            ...sortCriteria.current,
            ...(sortCriteria.current.cursor
                ? { cursor: sortCriteria.current.cursor }
                : {}),
            limit: sortCriteria.current.limit ?? DEFAULT_LIMIT,
        };

        return {
            ...fetchParams,
            sortCriteria: resolvedSortCriteria,
        };
    }, [fetchParams, sortCriteria.current]);

    const updateItem = (
        key = "id",
        value: number | string,
        update: T,
        partialUpdate?: Partial<T>,
    ) => {
        const itemIndex = items.current.findIndex((item) => item[key] == value);
        const _items = items.current.slice();
        if (itemIndex > -1) {
            _items[itemIndex] = partialUpdate
                ? { ..._items[itemIndex], ...partialUpdate }
                : update;
        } else {
            _items.unshift(update);
        }
        setItems(_items);
    };

    const insertItemAtIndex = (index: number, item: T) => {
        items.current.splice(index, 0, item);
    };

    const deleteItem = (key = "id", value: number | string) => {
        const itemIndex = items.current.findIndex((item) => item[key] == value);
        deleteItemByOrderIndex(itemIndex);
    };

    const deleteItemByOrderIndex = (itemIndex: number) => {
        const _items = items.current.slice();
        if (itemIndex > -1) {
            _items.splice(itemIndex, 1);
            setItems(_items);
        }
    };

    const updateNextPageReference = (
        cursor?: PostCursorableType | CommentCursorableType,
    ) => {
        setSortCriteria({
            ...sortCriteria.current,
            cursor: cursor ?? updatedFetchParams.sortCriteria?.cursor,
        });
    };

    async function loadMore() {
        if (items.current.length >= maxItemCount) {
            setHasNextPage(false);
            return;
        }
        setError(undefined);
        setLoading(true);
        setIsInitDone(false);
        try {
            const newItems = await fetcher(updatedFetchParams, preparedParams);
            const lastItem = newItems[newItems.length - 1];
            updateNextPageReference(lastItem?.cursor);
            setItems([...items.current, ...newItems]);
            setHasNextPage(
                newItems?.length >= updatedFetchParams.sortCriteria.limit,
            );
        } catch (err) {
            setError(err);
        } finally {
            setIsInitDone(true);
        }
    }

    const resetParams = (newFetchParams: FetchParams) => {
        setIsInitDone(false);
        setLoading(false);
        setItems([]);
        setHasNextPage(true);
        setSortCriteria(
            newFetchParams.sortCriteria ?? fetchParams.sortCriteria ?? {},
        );
    };

    return {
        isInitDone,
        loading,
        items: items.current,
        hasNextPage,
        error,
        loadMore,
        updateItem,
        deleteItem,
        resetParams,
        deleteItemByOrderIndex,
        insertItemAtIndex,
    };
}
